import { I18n } from 'utils/i18n';
import nth from 'utils/nth';
import getPdfjs from 'utils/pdfjs';
import { DOMParser } from '@xmldom/xmldom';
import xpath from 'xpath2';

/** Word delimiter for box tokenization — keep in sync with `getPartsFromPDF` and UI `position` derivation. */
export const BOX_ITEM_WORD_SPLIT = /[^\w-/]+/;

/**
 * Axis-aligned bounds for a `getTextContent` item in viewport space.
 * Uses `pdfjs.Util.transform` when available (handles rotation); falls back to raw transform math.
 */
function boxItemViewportBBox(item, viewport, pdfjs) {
  if (viewport?.transform && pdfjs?.Util?.transform) {
    const m = pdfjs.Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(m[2], m[3]) || 10;
    const left = m[4];
    const bottom = m[5];
    const top = bottom - fontHeight;
    // pdfjs `item.width` is already in scale=1 viewport (device) units; the
    // matrix scale is the font size, so multiplying expands every text item's
    // bbox across the page and a tight box catches multiple cells.
    const right = left + (item.width || 0);
    return { left, right, top, bottom };
  }
  const t = item.transform;
  const x = t[4] - viewport.viewBox[0];
  const y = viewport.viewBox[3] - (t[5] + t[3]);
  const w = item.width || 0;
  const h = item.height != null ? item.height : Math.max(Math.abs(t[3]), 8);
  return { left: x, right: x + w, top: y - h, bottom: y };
}

function touches(selBox, itemRect) {
  if (selBox.left > itemRect.right || itemRect.left > selBox.right) return false;
  if (selBox.top > itemRect.bottom || itemRect.top > selBox.bottom) return false;
  const overlapH = Math.min(selBox.bottom, itemRect.bottom) - Math.max(selBox.top, itemRect.top);
  const itemH = itemRect.bottom - itemRect.top;
  if (itemH > 0 && overlapH / itemH <= 0.6) return false;
  return true;
}

/**
 * Text runs whose bbox overlaps the padded selection box, sorted like `textInBox`,
 * with each run's substring range in the merged string (join `''`, same as extraction).
 */
function collectTextHitsInBox(textContent, viewport, box, pdfjs, padPx = 1, spans = null) {
  const b = {
    left: Math.min(box.left, box.right) - padPx,
    right: Math.max(box.left, box.right) + padPx,
    top: Math.min(box.top, box.bottom) - padPx,
    bottom: Math.max(box.top, box.bottom) + padPx,
  };
  const items = spans?.length
    ? spans
    : textContent.items
        .filter(ti => ti.str)
        .map(ti => ({ str: ti.str, ...boxItemViewportBBox(ti, viewport, pdfjs) }));
  const hits = [];
  for (const it of items) {
    if (!it.str) continue;
    if (!touches(b, it)) continue;
    hits.push({
      str: it.str,
      left: it.left,
      right: it.right,
      top: it.top,
      bottom: it.bottom,
      midY: (it.top + it.bottom) / 2,
    });
  }
  hits.sort((a, bb) => a.midY - bb.midY || a.left - bb.left);
  let off = 0;
  const hitRanges = hits.map(h => {
    const start = off;
    const end = off + h.str.length;
    off = end;
    return {
      start,
      end,
      str: h.str,
      left: h.left,
      right: h.right,
      top: h.top,
      bottom: h.bottom,
    };
  });
  const merged = hits.map(h => h.str).join('');
  return { merged, hitRanges };
}

/**
 * Collect text from spans whose bbox overlaps the selection box (>60% vertical).
 * Uses scratch TextLayer spans when available (per-word precision matching the preview),
 * falls back to raw `getTextContent` items.
 */
function textInBox(textContent, viewport, box, pdfjs, padPx = 1, spans = null) {
  return collectTextHitsInBox(textContent, viewport, box, pdfjs, padPx, spans).merged;
}

/**
 * Bbox for merged-string characters [rStart, rEnd). When a single pdf.js item spans multiple
 * tokens (e.g. "NIF. A08331290"), using the item's full width for each token makes every token
 * share the same box — overlap ties and index 0 always wins. Slice each hit horizontally in
 * proportion to the overlapping character indices (uniform width per char within the hit).
 */
function mergeHitBboxForCharRange(rStart, rEnd, hitRanges) {
  let minL = Infinity;
  let maxR = -Infinity;
  let minT = Infinity;
  let maxB = -Infinity;
  for (const hr of hitRanges) {
    const ol = Math.max(rStart, hr.start);
    const oe = Math.min(rEnd, hr.end);
    if (oe <= ol) continue;
    const hitLen = hr.end - hr.start;
    if (hitLen <= 0) continue;
    const t0 = (ol - hr.start) / hitLen;
    const t1 = (oe - hr.start) / hitLen;
    const w = hr.right - hr.left;
    const subLeft = hr.left + w * t0;
    const subRight = hr.left + w * t1;
    minL = Math.min(minL, subLeft);
    maxR = Math.max(maxR, subRight);
    minT = Math.min(minT, hr.top);
    maxB = Math.max(maxB, hr.bottom);
  }
  if (minL === Infinity) return null;
  return { left: minL, right: maxR, top: minT, bottom: maxB };
}

function boxWordTokenCharRanges(merged) {
  const arr = merged.split(BOX_ITEM_WORD_SPLIT).filter(Boolean);
  let pos0 = 0;
  const ranges = [];
  for (const tok of arr) {
    while (pos0 < merged.length) {
      const m = merged.slice(pos0).match(BOX_ITEM_WORD_SPLIT);
      if (m && m.index === 0) {
        pos0 += m[0].length;
        continue;
      }
      break;
    }
    if (pos0 + tok.length > merged.length || merged.slice(pos0, pos0 + tok.length) !== tok) {
      break;
    }
    ranges.push({ start: pos0, end: pos0 + tok.length, text: tok });
    pos0 += tok.length;
  }
  return ranges;
}

function horizOverlapWidth(a, b) {
  return Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
}

/**
 * After the user moves/resizes a routing box, re-derive `position` as an index into
 * `textInBox(...).split(BOX_ITEM_WORD_SPLIT)` — the same token list `getPartsFromPDF` uses for
 * `selector === 'box'`. Chooses the token whose bbox (per-hit slices proportional to character
 * span when one pdf item covers several tokens) has the strongest horizontal overlap with the
 * box (tie-break: horizontal centre closest to the box centre).
 */
export async function inferRoutingBoxPositionFromViewportBox(arrayBuffer, pageNum, coords) {
  if (!arrayBuffer?.byteLength || !coords || coords.left == null) {
    return null;
  }
  let loadingTask = null;
  try {
    const pdfjs = await getPdfjs();
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    const p = Math.min(Math.max(1, Number(pageNum) || 1), pdf.numPages);
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 1, rotation: page.rotate });
    const textContent = await page.getTextContent();
    const { merged, hitRanges } = collectTextHitsInBox(textContent, viewport, coords, pdfjs);
    if (!merged?.length || !hitRanges.length) {
      return { position: 0 };
    }
    const toks = boxWordTokenCharRanges(merged);
    if (!toks.length) {
      return { position: 0 };
    }
    if (toks.length === 1) {
      return { position: 0 };
    }
    const b = {
      left: Math.min(coords.left, coords.right),
      right: Math.max(coords.left, coords.right),
      top: Math.min(coords.top, coords.bottom),
      bottom: Math.max(coords.top, coords.bottom),
    };
    const boxCx = (b.left + b.right) / 2;
    let bestIdx = 0;
    let bestOv = -1;
    let bestDist = Infinity;
    for (let i = 0; i < toks.length; i++) {
      const bb = mergeHitBboxForCharRange(toks[i].start, toks[i].end, hitRanges);
      if (!bb) continue;
      const ov = horizOverlapWidth(bb, b);
      const cx = (bb.left + bb.right) / 2;
      const dist = Math.abs(cx - boxCx);
      if (ov > bestOv + 1e-6 || (Math.abs(ov - bestOv) < 1e-6 && dist < bestDist - 1e-6)) {
        bestOv = ov;
        bestDist = dist;
        bestIdx = i;
      }
    }
    return { position: bestIdx };
  } catch (e) {
    console.warn('inferRoutingBoxPositionFromViewportBox', e);
    return null;
  } finally {
    loadingTask?.destroy?.();
    loadingTask?._worker?.destroy?.();
  }
}

/**
 * Derives `join` and `position` for box items using the same rules as `getPartsFromPDF`: `textContent`
 * per span, try join "" then " ", `split` + `filter(Boolean)`, `indexOf(selectedText)`; then `reverse`
 * applies the same offset as before (`pos -= tokens.length`).
 * Pass the same elements `rectangleSelect` returns (or, for direction toggle, `cloneContents` spans).
 */
export function getBoxJoinAndPositionFromSpanElements(spanElements, selectedText, reverse = false) {
  if (!spanElements?.length || selectedText == null || selectedText === '') {
    return { join: '', pos: null };
  }
  const spans = spanElements.map(el => (el.textContent ?? '').trim());
  const tokenize = j => spans.join(j).split(BOX_ITEM_WORD_SPLIT).filter(Boolean);
  let join = '';
  let tokens = tokenize('');
  let pos = tokens.indexOf(selectedText);
  if (pos < 0) {
    join = ' ';
    tokens = tokenize(' ');
    pos = tokens.indexOf(selectedText);
  }
  if (pos < 0) {
    return { join: '', pos: null };
  }
  if (reverse) {
    pos -= tokens.length;
  }
  return { join, pos };
}

export const getXPathValue = async (xml, xp) => {
  const doc = new DOMParser().parseFromString(xml);
  return xpath.evaluate(`string(${xp})`, doc).stringValue;
};

export const getPartsFromXML = async (file, xml, format, partDone = () => null) => {
  const doc = new DOMParser().parseFromString(xml);
  const values = format.items.reduce((ret, { name, selector, xpath: xp }) => {
    if (selector === 'user') {
      return ret;
    }
    return { ...ret, [name]: xpath.evaluate(`string(${xp})`, doc).stringValue };
  }, {});
  partDone(values, 1);
  return values;
};

const getWordsFromPage = async (pdf, pageNum) => {
  const page = await pdf.getPage(pageNum);
  const viewport = page.getViewport({ scale: 1, rotation: page.rotate });
  const textContent = await page.getTextContent();
  const pdfjs = await getPdfjs();

  const words = textContent.items.reduce((ret, item) => {
    const x = item.transform[4] - viewport.viewBox[0];
    const y = viewport.viewBox[3] - (item.transform[5] + item.transform[3]);
    return {
      ...ret,
      [`${x},${y},${item.width},${item.height}`]: item.str,
    };
  }, []);

  return { words, viewport, textContent, pdfjs };
};

const getPartsFromPDF = async (file, pdf, format, partDone = () => null) => {
  const split = format.type === 'single-pdf';
  const pageNumbers = split ? Array.from({ length: pdf.numPages }, (_, i) => i + 1) : [1];

  const partsPromises = pageNumbers.map(async pageNum => {
    const { words, viewport, textContent, pdfjs } = await getWordsFromPage(pdf, pageNum);
    const parts = format.items.reduce((acc, item) => {
      try {
        let arr;
        if (item.selector === 'user') {
          return acc;
        }
        if (item.selector === 'box') {
          const merged = textInBox(textContent, viewport, item.coords, pdfjs);
          arr = merged ? merged.split(BOX_ITEM_WORD_SPLIT).filter(Boolean) : [];
        } else if (item.selector === 'coords' && item.coords.match(/>/)) {
          const [{ n: x, gt: gtx }, { n: y, gt: gty }] = item.coords
            .split(',')
            .map(n => ({ n: +n.replace('>', ''), gt: n.startsWith('>') }));
          [, arr] = Object.entries(words).find(([key]) => {
            const [wx, wy] = key.split(',').map(n => +n);
            return (gtx ? wx > x : wx === x) && (gty ? wy > y : y === wy);
          });
        } else if (item.selector === 'fileName') {
          const splitRegexp = new RegExp(`[${item.separators}]`);
          arr = file.split(splitRegexp);
        } else {
          [, arr] = Object.entries(words).find(([key]) => key.startsWith(item.coords)) || [];
        }
        arr = typeof arr === 'string' ? arr.trim().split(BOX_ITEM_WORD_SPLIT) : arr;
        let w;
        if (item.selector === 'box') {
          const want = item.position != null ? +item.position : 0;
          const maxIdx = Math.max(0, arr.length - 1);
          const idx = Math.min(want, maxIdx);
          w = nth(arr, idx);
        } else {
          w = nth(arr, item.position);
        }
        const raw = w != null && typeof w === 'string' ? w : '';
        const normalized = raw.replace(/[^ \w-/]/g, '').trim();
        return { ...acc, [item.name]: normalized };
      } catch {
        return { ...acc, [item.name]: '' };
      }
    }, {});

    await partDone(parts, pageNum);
    return parts;
  });

  const results = await Promise.all(split ? partsPromises : [partsPromises[0]]);
  return results;
};

export const getFileName = (partsIn, format) => {
  const parts = { ...partsIn };
  const partMap = format.items.reduce((acc, item) => ({ ...acc, [item.name]: item }), {});
  format.items
    .filter(i => i.selector === 'user')
    .forEach(item => {
      if (!parts[item.name]) {
        const value = I18n.t(`HrBot.fields.${item.name}`);
        parts[item.name] = `{${value}}`;
      }
    });
  let { year, month, day } = parts;
  let sendBy = parts[format.sendBy]?.replace(/\//g, '-');
  const { dni } = parts;
  if (year?.length < 4) {
    year = `20${year}`;
  }
  sendBy = sendBy ? sendBy.padStart(5, '0') : dni;
  year = partMap.year && year ? `${year}_` : '';
  month = partMap.month && month ? `${month}_` : '';
  day = partMap.day && day ? `${day}_` : '';
  let pref = format.prefix || '';
  if (pref && !pref.endsWith('_')) {
    pref += '_';
  }
  pref += `${year}${month}${day}`;
  return sendBy ? `${pref}${sendBy}.pdf` : '';
};

const getPartFunctions = {
  'multi-pdf': getPartsFromPDF,
  'single-pdf': getPartsFromPDF,
  xml: getPartsFromXML,
};

export const getParts = async (file, fileContents, format, partDone = () => null) => {
  return getPartFunctions[format.type](file, fileContents, format, partDone);
};

/**
 * Spreading `...parts` onto `foundItems` must not wipe a non-empty value just set in
 * `handleMouseUp` (refresh runs when `itemsBoxSig` updates after each draw).
 */
export function mergeExtractedPartsPreserveNonEmpty(prev, parts) {
  const next = { ...prev };
  Object.entries(parts).forEach(([key, value]) => {
    if (value == null) return;
    const t = typeof value === 'string' ? value.trim() : String(value);
    if (t !== '') {
      next[key] = typeof value === 'string' ? value.trim() : value;
    }
  });
  return next;
}

/**
 * Re-extract box fields from the PDF on reopen / refresh so `foundItems` is populated
 * (without this, reopening "encontrar campos" shows "No encontrado").
 */
export async function refreshBoxFoundPartsFromPdf(file, formatWithItems, pageNum, onParts) {
  if (!file || !formatWithItems?.items?.length) return;
  if (!['single-pdf', 'multi-pdf'].includes(formatWithItems.type)) return;
  const hasBox = formatWithItems.items.some(
    i => i.selector === 'box' && i.coords && typeof i.coords === 'object' && i.coords.left != null,
  );
  if (!hasBox) return;

  let loadingTask = null;
  try {
    const buf = await file.arrayBuffer();
    const pdfjs = await getPdfjs();
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(buf) });
    const pdf = await loadingTask.promise;

    await getParts(file.name, pdf, formatWithItems, async (parts, pageIdx) => {
      if (pageIdx !== pageNum) return;
      onParts(parts);
    });
  } catch (e) {
    console.warn('refreshBoxFoundPartsFromPdf', e);
  } finally {
    loadingTask?.destroy?.();
    loadingTask?._worker?.destroy?.();
  }
}

/** Same non-empty rule as `getPartsFromPDF` normalized extraction. */
function normalizedExtractedPresent(val) {
  if (val == null) return false;
  const t = typeof val === 'string' ? val.replace(/[^ \w-/]/g, '').trim() : String(val).trim();
  return t.length > 0;
}

/**
 * Runs the same per-page box extraction as the send flow and counts how many pages yield
 * non-empty text for `itemName` (split / single-pdf → every page; multi-pdf → page 1 only).
 */
export async function scanBoxItemAcrossPdfPages(arrayBuffer, fileLabel, formatWithItems, itemName) {
  if (!arrayBuffer?.byteLength || !formatWithItems?.items?.length) {
    return { totalPages: 0, successPages: 0, preview: '' };
  }
  if (!['single-pdf', 'multi-pdf'].includes(formatWithItems.type)) {
    return { totalPages: 0, successPages: 0, preview: '' };
  }
  const items = Array.isArray(formatWithItems.items) ? formatWithItems.items : [];
  const target = items.find(
    i =>
      i.name === itemName &&
      i.selector === 'box' &&
      i.coords &&
      typeof i.coords === 'object' &&
      i.coords.left != null,
  );
  if (!target) {
    return { totalPages: 0, successPages: 0, preview: '' };
  }

  let loadingTask = null;
  try {
    const pdfjs = await getPdfjs();
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    const dummyFile = { name: fileLabel || 'document.pdf' };
    let successPages = 0;
    let preview = '';
    await getParts(dummyFile, pdf, formatWithItems, async parts => {
      const raw = parts[itemName];
      if (normalizedExtractedPresent(raw)) {
        successPages += 1;
        if (!preview && typeof raw === 'string') {
          preview = raw.replace(/\s+/g, ' ').trim().slice(0, 72);
        }
      }
    });
    const totalPages = formatWithItems.type === 'single-pdf' ? pdf.numPages : 1;
    return { totalPages, successPages, preview };
  } catch (e) {
    console.warn('scanBoxItemAcrossPdfPages', e);
    return { totalPages: 0, successPages: 0, preview: '', error: true };
  } finally {
    loadingTask?.destroy?.();
    loadingTask?._worker?.destroy?.();
  }
}

async function extractNormalizedBoxItemValueAtPage(pdf, pageNum, item) {
  if (item.selector !== 'box' || !item.coords || item.coords.left == null) return '';
  const { viewport, textContent, pdfjs } = await getWordsFromPage(pdf, pageNum);
  try {
    const merged = textInBox(textContent, viewport, item.coords, pdfjs);
    const arr = merged ? merged.split(BOX_ITEM_WORD_SPLIT).filter(Boolean) : [];
    const want = item.position != null ? +item.position : 0;
    const maxIdx = Math.max(0, arr.length - 1);
    const idx = Math.min(want, maxIdx);
    const w = nth(arr, idx);
    const raw = w != null && typeof w === 'string' ? w : '';
    return raw.replace(/[^ \w-/]/g, '').trim();
  } catch {
    return '';
  }
}

/**
 * One PDF.js open for all routing boxes — avoids destroying/reopening the worker between items
 * (sequential `getBoxItemNormalizedTextOnPage` calls could leave later extractions empty).
 */
export async function getRoutingBoxPreviewTexts(arrayBuffer, formatWithItems) {
  if (!arrayBuffer?.byteLength || !formatWithItems?.items?.length) return {};
  if (!['single-pdf', 'multi-pdf'].includes(formatWithItems.type)) return {};
  const items = Array.isArray(formatWithItems.items) ? formatWithItems.items : [];
  const boxItems = items.filter(
    i => i.selector === 'box' && i.coords && typeof i.coords === 'object' && i.coords.left != null,
  );
  if (!boxItems.length) return {};

  let loadingTask = null;
  try {
    const pdfjs = await getPdfjs();
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    const out = {};
    for (const it of boxItems) {
      const p = Math.min(Math.max(1, Number(it.page) || 1), pdf.numPages);
      out[it.name] = await extractNormalizedBoxItemValueAtPage(pdf, p, it);
    }
    return out;
  } catch (e) {
    console.warn('getRoutingBoxPreviewTexts', e);
    return {};
  } finally {
    loadingTask?.destroy?.();
    loadingTask?._worker?.destroy?.();
  }
}

/**
 * Normalized extracted text for one box item on a single page (same rules as `getPartsFromPDF`).
 */
export async function getBoxItemNormalizedTextOnPage(
  arrayBuffer,
  formatWithItems,
  itemName,
  pageNum,
) {
  if (!arrayBuffer?.byteLength || !formatWithItems?.items?.length) return '';
  if (!['single-pdf', 'multi-pdf'].includes(formatWithItems.type)) return '';
  const items = Array.isArray(formatWithItems.items) ? formatWithItems.items : [];
  const item = items.find(
    i =>
      i.name === itemName &&
      i.selector === 'box' &&
      i.coords &&
      typeof i.coords === 'object' &&
      i.coords.left != null,
  );
  if (!item) return '';

  let loadingTask = null;
  try {
    const pdfjs = await getPdfjs();
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    const p = Math.min(Math.max(1, Number(pageNum) || 1), pdf.numPages);
    return await extractNormalizedBoxItemValueAtPage(pdf, p, item);
  } catch (e) {
    console.warn('getBoxItemNormalizedTextOnPage', e);
    return '';
  } finally {
    loadingTask?.destroy?.();
    loadingTask?._worker?.destroy?.();
  }
}
